/**
 * useBetaGate Hook
 * Provides action interception for unauthenticated users or non-beta users
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { analytics } from '../lib/posthog';

export const useBetaGate = () => {
  const { user, authReady } = useAuth();
  const loading = !authReady;
  const isBeta = true; // Everyone is beta user in new auth system
  const [modalOpen, setModalOpen] = useState(false);
  const [triggerAction, setTriggerAction] = useState(null);

  // Check if user has access to perform actions
  const hasAccess = useCallback(() => {
    if (loading) return false; // Don't allow actions during loading
    if (!user) return false;   // Require authentication
    return isBeta;             // Require beta access
  }, [user, loading, isBeta]);

  // Gate an action - returns true if action can proceed, false if gated
  const gateAction = useCallback((actionName) => {
    if (hasAccess()) {
      // User has access, allow action to proceed
      return true;
    }

    // Gate the action - show waitlist modal
    setTriggerAction(actionName);
    setModalOpen(true);
    
    // Track gated action attempt
    analytics.track('action_gated', {
      action: actionName,
      user_authenticated: !!user,
      user_has_beta: !!isBeta,
      timestamp: new Date().toISOString()
    });

    return false; // Prevent action from proceeding
  }, [hasAccess, user, isBeta]);

  // Convenience wrapper for async actions
  const gateAsyncAction = useCallback(async (actionName, actionFn) => {
    if (gateAction(actionName)) {
      return await actionFn();
    }
    return null; // Action was gated
  }, [gateAction]);

  // Close modal
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setTriggerAction(null);
  }, []);

  return {
    // State
    hasAccess: hasAccess(),
    modalOpen,
    triggerAction,
    
    // Actions
    gateAction,
    gateAsyncAction,
    closeModal,
    
    // User info for conditional rendering
    isAuthenticated: !!user,
    isLoading: loading,
    isBetaUser: !!isBeta
  };
};

// Higher-order component for gating entire components
export const withBetaGate = (Component, fallbackComponent = null) => {
  return function BetaGatedComponent(props) {
    const { hasAccess } = useBetaGate();
    
    if (hasAccess) {
      return <Component {...props} />;
    }
    
    if (fallbackComponent) {
      return fallbackComponent;
    }
    
    // Default fallback - empty div (component won't render)
    return <div></div>;
  };
};

// Action gate wrapper for specific functions
export const useActionGate = () => {
  const { gateAction, gateAsyncAction } = useBetaGate();
  
  return {
    // Common gated actions
    gateAddDebt: () => gateAction('add_debt'),
    gateUpdateDebt: () => gateAction('update_debt'),
    gateDeleteDebt: () => gateAction('delete_debt'),
    gateSaveProfile: () => gateAction('save_profile'),
    gateAICoach: () => gateAction('ai_coach'),
    gateAIReport: () => gateAction('ai_report'),
    gateWhatIf: () => gateAction('what_if_machine'),
    gateMoneyMakeover: () => gateAction('money_makeover'),
    gateExportData: () => gateAction('export_data'),
    
    // Generic action gating
    gateAction,
    gateAsyncAction
  };
};

export default useBetaGate;